import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { ItineraryItem, CheckoutPayload } from '../types/bot.types';

export interface BotExtState {
  sessionId: string | null;
  sessionStatus: 'active' | 'archived' | 'blocked' | null;
  selections: ItineraryItem[];
  totalPrice: number;
  currency: string;
  checkoutReady: boolean;
  checkoutPayload: CheckoutPayload | null;
  /** Expiry from generate_checkout_url / session.metadata.last_checkout */
  checkoutExpiresAt: string | null;
  /** Cache of `sessionId → tab label` so tab strip labels survive tab
   *  switches (the active session's first-user-message text is only in
   *  memory while that session is loaded). Populated on session load and
   *  whenever a user message lands. */
  tabTitles: Record<string, string>;
  /** Epoch-ms deadline after which a NEW session may be created again, set when
   *  the BE rejects `POST /sessions` with its per-IP quota. Null = no cooldown.
   *
   *  Only new-session creation is affected — sessions that already exist keep
   *  working, so this never gates the whole widget (see useNewSessionCooldown). */
  newSessionCooldownUntil: number | null;
  /** Last non-quota session-creation failure, so the widget can say *something*
   *  useful instead of leaving the user's message hanging. Cleared on success. */
  sessionCreateFailed: boolean;
  /** The live session's conversation language from the BE (a primary subtag
   *  such as 'he'), or null when unknown. It sets assistant-bubble direction:
   *  see conversationDirection.ts. Comes from the session payload on load and
   *  from `bot:done` after every turn. Cleared by RESET_BOT. */
  conversationLanguage: string | null;
}

export type BotExtAction =
  | { type: 'SET_SESSION'; sessionId: string; status: BotExtState['sessionStatus'] }
  | { type: 'SET_SESSION_STATUS'; status: BotExtState['sessionStatus'] }
  | { type: 'SET_SELECTIONS'; selections: ItineraryItem[]; totalPrice: number; currency: string }
  | { type: 'SET_CHECKOUT'; payload: CheckoutPayload; expiresAt?: string | null }
  /** Hide the panel but keep `checkoutPayload` so the header chip can re-open
   *  it instantly. Used by the panel's close (X) button. */
  | { type: 'HIDE_CHECKOUT' }
  /** Re-show the panel using whatever `checkoutPayload` is already in state.
   *  Used by the header "Preview cart" chip. No-op if payload is empty. */
  | { type: 'SHOW_CHECKOUT' }
  /** Discard both the panel visibility AND the payload. Used on session
   *  reset / new chat so a stale checkout doesn't bleed across sessions. */
  | { type: 'CLEAR_CHECKOUT' }
  /** Update the cached tab label for a session. Idempotent — re-setting
   *  the same title is a no-op. Setting empty string is rejected so we
   *  don't overwrite a good label with a placeholder. */
  | { type: 'SET_TAB_TITLE'; sessionId: string; title: string }
  /** Drop the cached label for a session (called when a tab closes). */
  | { type: 'CLEAR_TAB_TITLE'; sessionId: string }
  /** Start (or rehydrate) the new-session cooldown. `until` is epoch ms. */
  | { type: 'SET_NEW_SESSION_COOLDOWN'; until: number }
  /** Cooldown elapsed (or the BE let a session through) — re-enable creation. */
  | { type: 'CLEAR_NEW_SESSION_COOLDOWN' }
  /** Flag/unflag a generic (non-quota) session-creation failure. */
  | { type: 'SET_SESSION_CREATE_FAILED'; failed: boolean }
  /** The BE's conversation language for the live session (null = unknown). */
  | { type: 'SET_CONVERSATION_LANGUAGE'; language: string | null }
  | { type: 'RESET_BOT' };

const initialBotState: BotExtState = {
  sessionId: null,
  sessionStatus: null,
  selections: [],
  totalPrice: 0,
  currency: 'USD',
  checkoutReady: false,
  checkoutPayload: null,
  checkoutExpiresAt: null,
  tabTitles: {},
  newSessionCooldownUntil: null,
  sessionCreateFailed: false,
  conversationLanguage: null,
};

function botExtReducer(state: BotExtState, action: BotExtAction): BotExtState {
  switch (action.type) {
    case 'SET_SESSION':
      // A session was created/loaded, so whatever blocked creation is over.
      return {
        ...state,
        sessionId: action.sessionId,
        sessionStatus: action.status,
        newSessionCooldownUntil: null,
        sessionCreateFailed: false,
      };
    case 'SET_SESSION_STATUS':
      return { ...state, sessionStatus: action.status };
    case 'SET_SELECTIONS':
      return { ...state, selections: action.selections, totalPrice: action.totalPrice, currency: action.currency };
    case 'SET_CHECKOUT':
      return {
        ...state,
        checkoutReady: true,
        checkoutPayload: action.payload,
        ...(action.expiresAt !== undefined
          ? { checkoutExpiresAt: action.expiresAt }
          : {}),
      };
    case 'HIDE_CHECKOUT':
      return { ...state, checkoutReady: false };
    case 'SHOW_CHECKOUT':
      // No-op if there's nothing to show; prevents the chip from opening an
      // empty panel.
      return state.checkoutPayload ? { ...state, checkoutReady: true } : state;
    case 'CLEAR_CHECKOUT':
      return {
        ...state,
        checkoutReady: false,
        checkoutPayload: null,
        checkoutExpiresAt: null,
      };
    case 'SET_TAB_TITLE': {
      const t = action.title?.trim();
      if (!t || state.tabTitles[action.sessionId] === t) return state;
      return { ...state, tabTitles: { ...state.tabTitles, [action.sessionId]: t } };
    }
    case 'CLEAR_TAB_TITLE': {
      if (!(action.sessionId in state.tabTitles)) return state;
      const next = { ...state.tabTitles };
      delete next[action.sessionId];
      return { ...state, tabTitles: next };
    }
    case 'SET_NEW_SESSION_COOLDOWN':
      if (state.newSessionCooldownUntil === action.until) return state;
      return { ...state, newSessionCooldownUntil: action.until, sessionCreateFailed: false };
    case 'CLEAR_NEW_SESSION_COOLDOWN':
      if (state.newSessionCooldownUntil === null) return state;
      return { ...state, newSessionCooldownUntil: null };
    case 'SET_SESSION_CREATE_FAILED':
      if (state.sessionCreateFailed === action.failed) return state;
      return { ...state, sessionCreateFailed: action.failed };
    case 'SET_CONVERSATION_LANGUAGE':
      if (state.conversationLanguage === action.language) return state;
      return { ...state, conversationLanguage: action.language };
    case 'RESET_BOT':
      // Keep tabTitles across full state resets so a tab switch (which
      // also dispatches RESET_BOT to clear sessionId / selections / etc.)
      // doesn't blow away the cached labels for OTHER tabs.
      //
      // The cooldown survives too, and for a stronger reason: it is a per-IP
      // server-side quota, not session state. Clearing it on "new chat" would
      // hand the user an enabled input that is guaranteed to 403 again.
      return {
        ...initialBotState,
        tabTitles: state.tabTitles,
        newSessionCooldownUntil: state.newSessionCooldownUntil,
      };
    default:
      return state;
  }
}

interface BotContextValue {
  botState: BotExtState;
  botDispatch: React.Dispatch<BotExtAction>;
}

const BotContext = createContext<BotContextValue | null>(null);

export const BotExtProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [botState, botDispatch] = useReducer(botExtReducer, initialBotState);
  return <BotContext.Provider value={{ botState, botDispatch }}>{children}</BotContext.Provider>;
};

export function useBotContext(): BotContextValue {
  const ctx = useContext(BotContext);
  if (!ctx) throw new Error('useBotContext must be inside BotExtProvider');
  return ctx;
}
